"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { openConsentSettings } from "@/lib/consent";
import { t } from "@/lib/site-texts";

export default function Footer() {
  const pathname = usePathname();
  const st = useSiteSettings();
  if (pathname?.startsWith("/admin")) return null;
  return (
    <footer className="site-footer">
      {/* USP Balk */}
      <div className="usp-bar" style={{ background: "#fff" }}>
        <div className="container">
          <div className="usp-grid">
            <div className="usp-item">
              <div className="usp-icon">
                {/* Bezorgwagen icoon */}
                <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="12" width="28" height="22" rx="2" />
                  <path d="M30 18h8l6 8v6h-14V18z" />
                  <circle cx="10" cy="36" r="4" />
                  <circle cx="38" cy="36" r="4" />
                </svg>
              </div>
              <div className="usp-content">
                <h2>{st.usp1Title}</h2>
                <p>{st.usp1Text}</p>
              </div>
            </div>

            <div className="usp-item">
              <div className="usp-icon">
                <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="24" cy="24" r="20" />
                  <path d="M24 12v12l8 4" />
                </svg>
              </div>
              <div className="usp-content">
                <h2>{st.usp2Title}</h2>
                <p>{st.usp2Text}</p>
              </div>
            </div>

            <div className="usp-item">
              <div className="usp-icon">
                <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 12l10-6 12 8 12-8 10 6-6 10H8L2 12z" />
                  <rect x="16" y="14" width="16" height="26" rx="1" />
                </svg>
              </div>
              <div className="usp-content">
                <h2>{st.usp3Title}</h2>
                <p>{st.usp3Text}</p>
              </div>
            </div>

            <div className="usp-item">
              <div className="usp-icon">
                <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="10" width="40" height="28" rx="4" />
                  <path d="M4 18h40" />
                  <path d="M10 28h8" />
                  <path d="M10 33h5" />
                </svg>
              </div>
              <div className="usp-content">
                <h2>{st.usp4Title}</h2>
                <p>{st.usp4Text}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hoofd footer */}
      <div className="container">
        <div className="footer-main">
          {/* Kolom 1: Logo + beschrijving */}
          <div className="footer-logo-area">
            <Link href="/" style={{ textDecoration: "none", display: "inline-block" }}>
              <Image
                src="/images/logo-klein.png"
                data-i-key="global.logo"
                alt="Mijnondergoedwinkel.nl"
                width={135}
                height={45}
                style={{ display: "block", height: "45px", marginBottom: "10px", filter: "brightness(0) invert(1)", opacity: 0.9 }}
              />
            </Link>

            <p className="footer-tagline">{st.footerTagline}</p>

            <p className="footer-description" data-t-key="footer.description">
              {t(
                "footer.description",
                "Mijnondergoedwinkel.nl is dé ondergoedspecialist voor dames, heren en kinderen. Comfortabele basics, nachtmode en thermo van vertrouwde merken."
              )}
            </p>

            <div className="footer-social">
              <a
                href="https://www.facebook.com/mijnondergoedwinkel"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/mijnondergoedwinkel"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
              </a>
            </div>
          </div>

          {/* Kolom 2: Snel naar */}
          <div className="footer-column">
            <h2 data-t-key="footer.snel-naar.title">{t("footer.snel-naar.title", "Snel Naar")}</h2>
            <nav className="footer-links" aria-label="Snel naar links">
              <Link href="/shop">Shop</Link>
              <Link href="/product-category/dames/dames-ondergoed">Dames ondergoed</Link>
              <Link href="/product-category/heren/heren-ondergoed">Heren ondergoed</Link>
              <Link href="/product-category/nachtmode">Nachtmode</Link>
              <Link href="/shop?sale=1">Sale</Link>
              <Link href="/over-ons">Over ons</Link>
              {/* >>> MOW-OKT26 cmsquick: tijdelijke link naar het blogoverzicht (vervalt zodra het footermenu beheerbaar is) */}
              <Link href="/blog-inspiratie">Blog &amp; Inspiratie</Link>
              {/* <<< MOW-OKT26 cmsquick */}
              <Link href="/contact-ons">Contact</Link>
              <Link href="/merken">Merken</Link>
            </nav>
          </div>

          {/* Kolom 3: Klantenservice */}
          <div className="footer-column">
            <h2 data-t-key="footer.klantenservice.title">{t("footer.klantenservice.title", "Klantenservice")}</h2>
            <p className="footer-service-text" data-t-key="footer.klantenservice.body">
              {t("footer.klantenservice.body", "Heb je een vraag over maat, model of je bestelling? Wij helpen je graag verder.")}
            </p>
            <a href={`mailto:${st.footerEmail || "info@mijnondergoedwinkel.nl"}`} className="footer-contact-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              {st.footerEmail || "info@mijnondergoedwinkel.nl"}
            </a>
            <a href="tel:+31623399503" className="footer-contact-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              +31 6 23399503
            </a>

            {/* ── Wettelijke herroepingsknop (verplicht per 19 juni 2026) ── */}
            <Link
              href="/herroeping"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "14px",
                marginBottom: "4px",
                padding: "10px 14px",
                background: "transparent",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: "5px",
                textDecoration: "none",
                fontSize: "0.82rem",
                fontWeight: 600,
                letterSpacing: "0.03em",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--color-primary)";
                e.currentTarget.style.borderColor = "var(--color-primary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="1,4 1,10 7,10"/><path d="M3.51 15a9 9 0 1 0 .49-3.96"/>
              </svg>
              <span data-t-key="footer.herroeping.cta">{t("footer.herroeping.cta", "Aankoop annuleren / herroepen")}</span>
            </Link>
            <p style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", marginBottom: "12px", lineHeight: 1.4 }}>
              {st.returnDays} dagen retourrecht · Geen reden nodig
            </p>

            <nav className="footer-links" aria-label="Servicelinks">
              <Link href="/veelgestelde-vragen">Veelgestelde vragen</Link>
              <Link href="/bezorgen-retourneren">Bezorgen &amp; Retourneren</Link>
              <Link href="/herroeping">Herroepingsrecht</Link>
              <Link href="/privacybeleid">Privacybeleid</Link>
              <Link href="/algemene-voorwaarden">Algemene voorwaarden</Link>
              <button
                type="button"
                onClick={openConsentSettings}
                style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "inherit", cursor: "pointer", textAlign: "left" }}
              >
                Cookie-instellingen
              </button>
            </nav>
          </div>


          {/* Kolom 4: Goed om te weten */}
          <div className="footer-column">
            <h2 data-t-key="footer.goed-om-te-weten.title">{t("footer.goed-om-te-weten.title", "Goed om te Weten")}</h2>
            <ul className="footer-usp-list">
              {[st.footerUsp1, st.footerUsp2, st.footerUsp3].filter(Boolean).map((u, i) => (
                <li key={i}>{u}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Footer bottom balk */}
      <div className="footer-bottom">
        <div className="container">
          <div className="footer-bottom-inner">
            <p className="footer-copyright">
              © {new Date().getFullYear()}{" "}
              <span data-t-key="footer.copyright.suffix">{t("footer.copyright.suffix", "Mijnondergoedwinkel.nl — Alle rechten voorbehouden")}</span>
            </p>
            <div className="footer-payments" aria-label="Betaalmethoden">
              {/* Mastercard */}
              <svg className="payment-icon" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Mastercard" width="38" height="24">
                <rect width="38" height="24" rx="3" fill="#fff" stroke="#ddd" />
                <circle cx="14" cy="12" r="7" fill="#EB001B" />
                <circle cx="24" cy="12" r="7" fill="#F79E1B" />
                <path d="M19 7.5a7 7 0 0 1 0 9 7 7 0 0 1 0-9z" fill="#FF5F00" />
              </svg>
              {/* PayPal */}
              <svg className="payment-icon" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="PayPal" width="38" height="24">
                <rect width="38" height="24" rx="3" fill="#fff" stroke="#ddd" />
                <text x="5" y="16" fontFamily="Arial" fontSize="9" fontWeight="bold" fill="#003087">Pay</text>
                <text x="17" y="16" fontFamily="Arial" fontSize="9" fontWeight="bold" fill="#009cde">Pal</text>
              </svg>
              {/* Visa */}
              <svg className="payment-icon" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Visa" width="38" height="24">
                <rect width="38" height="24" rx="3" fill="#fff" stroke="#ddd" />
                <text x="7" y="16" fontFamily="Arial" fontSize="11" fontWeight="bold" fill="#1A1F71">VISA</text>
              </svg>
              {/* iDEAL */}
              <svg className="payment-icon" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="iDEAL" width="38" height="24">
                <rect width="38" height="24" rx="3" fill="#fff" stroke="#ddd" />
                <text x="5" y="16" fontFamily="Arial" fontSize="9" fontWeight="bold" fill="#cc0000">iDEAL</text>
              </svg>
              {/* Klarna */}
              <svg className="payment-icon" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Klarna" width="38" height="24">
                <rect width="38" height="24" rx="3" fill="#FFB3C7" />
                <text x="19" y="16" textAnchor="middle" fontFamily="Arial" fontSize="9" fontWeight="bold" fill="#0A0B09">Klarna.</text>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
